import React, { useState } from 'react';
import { Modal, Form, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useTransferir } from 'hooks/useContas';
import { ContaType, TransferirDto } from 'services/contaService';

interface ModalTransferirProps {
    show: boolean;
    onHide: () => void;
    contaOrigem: ContaType | null;
    contas: ContaType[];
}

const ModalTransferir: React.FC<ModalTransferirProps> = ({ show, onHide, contaOrigem, contas }) => {
    const [contaDestinoId, setContaDestinoId] = useState('');
    const [valor, setValor] = useState('');
    const [descricao, setDescricao] = useState('');
    const [erro, setErro] = useState('');

    const transferirMutation = useTransferir();

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const contasDestino = contas.filter(conta => conta.id !== contaOrigem?.id);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro('');

        if (!valor || parseFloat(valor) <= 0) {
            setErro('O valor deve ser maior que zero');
            return;
        }

        if (!contaOrigem) {
            setErro('Conta de origem não encontrada');
            return;
        }

        if (!contaDestinoId) {
            setErro('Selecione a conta de destino');
            return;
        }

        const valorNumerico = parseFloat(valor);
        if (valorNumerico > contaOrigem.saldo) {
            setErro('Saldo insuficiente para realizar a transferência');
            return;
        }

        try {
            const dto: TransferirDto = {
                contaOrigemId: contaOrigem.id,
                contaDestinoId: contaDestinoId,
                valor: valorNumerico,
                descricao: descricao.trim() || undefined
            };
            await transferirMutation.mutateAsync(dto);

            setContaDestinoId('');
            setValor('');
            setDescricao('');
            onHide();
        } catch (error: any) {
            setErro(error?.response?.data?.message || 'Erro ao realizar transferência');
        }
    };

    const handleClose = () => {
        setContaDestinoId('');
        setValor('');
        setDescricao('');
        setErro('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faExchangeAlt} className="me-2 text-info-emphasis" />
                    Transferir
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {erro && (
                        <Alert variant="danger" className="mb-3 border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">{erro}</div>
                        </Alert>
                    )}

                    {contaOrigem && (
                        <Alert variant="info" className="mb-3 border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <strong>De: {contaOrigem.nome}</strong><br />
                                Saldo disponível: <span className="text-success-emphasis">{formatarMoeda(contaOrigem.saldo)}</span>
                            </div>
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Para a Conta *</Form.Label>
                        <Form.Select
                            value={contaDestinoId}
                            onChange={(e) => setContaDestinoId(e.target.value)}
                            required
                            disabled={transferirMutation.isPending}
                        >
                            <option value="">Selecione a conta de destino</option>
                            {contasDestino.map(conta => (
                                <option key={conta.id} value={conta.id}>
                                    {conta.nome} - {formatarMoeda(conta.saldo)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Valor da Transferência *</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>R$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                                placeholder="0,00"
                                required
                                disabled={transferirMutation.isPending}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Descrição (opcional)</Form.Label>
                        <Form.Control
                            type="text"
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            placeholder="Ex: Transferência para pagamento..."
                            disabled={transferirMutation.isPending}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={transferirMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="info"
                        type="submit"
                        disabled={
                            transferirMutation.isPending ||
                            !valor ||
                            parseFloat(valor || '0') <= 0 ||
                            !contaDestinoId ||
                            contasDestino.length === 0
                        }
                    >
                        {transferirMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                                Confirmar Transferência
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalTransferir; 